'use client';

import { useEffect } from 'react';
import { initPerfLogging, logPerf } from '@/lib/perf';

type LongTaskSummary = {
  count: number;
  total: number;
  max: number;
};

export function PerfLogger() {
  useEffect(() => {
    const enabled = initPerfLogging();
    if (!enabled) return;

    logPerf('logger_mounted');

    const longTasks: LongTaskSummary = { count: 0, total: 0, max: 0 };

    const observers: PerformanceObserver[] = [];

    try {
      const paintObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          logPerf(`paint:${entry.name}`, { duration: entry.duration });
        });
      });
      paintObserver.observe({ type: 'paint', buffered: true });
      observers.push(paintObserver);
    } catch {}

    try {
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const last = entries[entries.length - 1];
        if (last) {
          logPerf('lcp', { value: last.startTime });
        }
      });
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
      observers.push(lcpObserver);
    } catch {}

    try {
      const longTaskObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          longTasks.count += 1;
          longTasks.total += entry.duration;
          longTasks.max = Math.max(longTasks.max, entry.duration);
        });
      });
      longTaskObserver.observe({ type: 'longtask', buffered: true });
      observers.push(longTaskObserver);
    } catch {}

    const logNavTiming = () => {
      const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
      if (!nav) return;
      logPerf('navigation', {
        domInteractive: nav.domInteractive,
        domContentLoaded: nav.domContentLoadedEventEnd,
        loadEventEnd: nav.loadEventEnd,
        transferSize: nav.transferSize,
      });
    };

    window.addEventListener('load', logNavTiming, { once: true });
    const summaryTimer = window.setTimeout(() => {
      logPerf('longtask_summary', {
        count: longTasks.count,
        total: Number(longTasks.total.toFixed(1)),
        max: Number(longTasks.max.toFixed(1)),
      });
    }, 6000);

    return () => {
      window.removeEventListener('load', logNavTiming);
      window.clearTimeout(summaryTimer);
      observers.forEach((o) => o.disconnect());
    };
  }, []);

  return null;
}
